<template>
    <div>
        <section class="pricing-policy-panel" aria-labelledby="pricing-policy-title">
            <div>
                <p class="eyebrow">Store-wide pricing policy</p>
                <h2 id="pricing-policy-title">Promotion stacking</h2>
                <p class="policy-copy">
                    Choose how regular product promotions combine. First-order, BOGO, and bundle offers remain exclusive.
                </p>
            </div>
            <div class="policy-actions">
                <div class="mode-picker" role="group" aria-label="Promotion pricing mode">
                    <button
                        type="button"
                        :class="{ active: pricingMode === 'best_price' }"
                        :disabled="pricingConfigLoading || pricingConfigSaving || !canEditPricingPolicy"
                        @click="savePricingMode('best_price')"
                    >
                        <strong>Best price</strong>
                        <span>One winning offer</span>
                    </button>
                    <button
                        type="button"
                        :class="{ active: pricingMode === 'cumulative' }"
                        :disabled="pricingConfigLoading || pricingConfigSaving || !canEditPricingPolicy"
                        @click="savePricingMode('cumulative')"
                    >
                        <strong>Cumulative</strong>
                        <span>Stack regular offers</span>
                    </button>
                </div>
                <small v-if="pricingConfigSaving">Saving policy…</small>
                <small v-else-if="!canEditPricingPolicy">View only — promotion edit permission required.</small>
                <small v-else>This store-wide policy applies to every POS cart.</small>
            </div>
        </section>

        <CrudIndex
            v-if="!loading"
            ref="crudIndex"
            title="Promotions"
            :viewColumns="columns"
            fetchUrl="/promotions/formatted"
            createUrl="/promotions/create"
            editRouteName="promotion_edit"
            navigateToCustomCreate
            updateRootUrl="/promotions"
            deleteRootUrl="/promotions"
            :createFields="createFields"
            :reloadOnSuccess="true"
            :getFormatter="formatPromotions"
            :defaultPerPage="20"
            :permission="PERMISSION_CODES.CMS_VIEW_PROMOTION"
            :customActions="customActions"
            customActionTitle="Copy promotion"
            customActionIcon="fa-copy"
            customActionIconOnly
            onlyBtnIcons
            :customActionPermission="PERMISSION_CODES.CMS_CREATE_PROMOTION"
            @handleCustomAction="copyPromotion"
        />

        <!-- Confirmation Modal for Deleting Expired Promotions -->
        <CustomModal
            v-if="showDeleteConfirmModal"
            :title="`Confirm Delete Expired Promotions`"
            :show="showDeleteConfirmModal"
            @close="showDeleteConfirmModal = false"
        >
            <div class="my-4">
                <!-- Loading state while fetching count -->
                <div v-if="loadingExpiredCount" class="text-center py-3">
                    <div class="spinner-border" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                    <p class="mt-2">Checking for expired promotions...</p>
                </div>

                <!-- Show confirmation message with count -->
                <div v-else-if="expiredPromotionsCount !== null">
                    <div v-if="expiredPromotionsCount === 0" class="alert alert-info">
                        <i class="fas fa-info-circle"></i>
                        <strong>No expired promotions found.</strong>
                        <p class="mb-0">There are currently no expired promotions to delete.</p>
                    </div>
                    <div v-else class="alert alert-warning">
                        <i class="fas fa-exclamation-triangle"></i>
                        <strong>Are you sure you want to delete expired promotions?</strong>
                        <p class="mb-0">
                            This action will permanently delete <strong>{{ expiredPromotionsCount }}</strong> 
                            expired promotion{{ expiredPromotionsCount > 1 ? 's' : '' }}. 
                            This action cannot be undone.
                        </p>
                    </div>
                </div>

                <!-- Error state -->
                <div v-else-if="previewError" class="alert alert-danger">
                    <i class="fas fa-exclamation-circle"></i>
                    <strong>Error loading expired promotions count.</strong>
                    <p class="mb-0">{{ previewError }}</p>
                </div>
            </div>

            <!-- Modal Actions -->
            <div class="text-right">
                <button type="button" class="btn btn-secondary mr-2" @click="cancelDeleteExpired">
                    Cancel
                </button>
                <button 
                    v-if="expiredPromotionsCount > 0 && !loadingExpiredCount && !deletingExpired"
                    type="button" 
                    class="btn btn-danger" 
                    @click="confirmDeleteExpired"
                >
                    <i class="fas fa-trash-alt"></i>
                    Delete {{ expiredPromotionsCount }} Promotion{{ expiredPromotionsCount > 1 ? 's' : '' }}
                </button>
                <button 
                    v-if="deletingExpired"
                    type="button" 
                    class="btn btn-danger" 
                    disabled
                >
                    <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                    Deleting...
                </button>
            </div>
        </CustomModal>
    </div>
</template>

<script>
import CrudIndex from "@/components/crud/index.vue"
import CustomModal from "@/components/layout/CustomModal.vue"
import { createField, createColumn } from "@/utils/common"
import { http } from "@/utils/http"
import { mapState } from "vuex"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"

export default {
    data() {
        return {
            PERMISSION_CODES,
            loading: true,
            customers: [],
            discountSlabs: [],
            createFields: [],
            // Delete expired promotions modal state
            showDeleteConfirmModal: false,
            loadingExpiredCount: false,
            expiredPromotionsCount: null,
            previewError: null,
            deletingExpired: false,
            pricingMode: "best_price",
            pricingConfigLoading: true,
            pricingConfigSaving: false,
            columns: [
                {
                    label: "Promotion",
                    field: "name",
                    type: "string",
                    secondaryField: "promotionTypeLabel",
                    thClass: "promotion-name-column",
                    tdClass: "promotion-name-column",
                },
                {
                    label: "Discount",
                    field: "discountSummary",
                    type: "string",
                    sortable: false,
                    thClass: "promotion-discount-column",
                    tdClass: "promotion-discount-column",
                },
                {
                    label: "POS",
                    field: "posVisibilityLabel",
                    type: "string",
                    sortable: false,
                    valueClassField: "posVisibilityClass",
                    thClass: "promotion-pos-column",
                    tdClass: "promotion-pos-column",
                },
                {
                    label: "Locations",
                    field: "locationSummary",
                    type: "string",
                    tooltipField: "locationDetails",
                    thClass: "promotion-locations-column",
                    tdClass: "promotion-locations-column",
                },
                {
                    label: "Status",
                    field: "statusLabel",
                    type: "string",
                    tooltipField: "statusDetails",
                    valueClassField: "statusClass",
                    thClass: "promotion-status-column",
                    tdClass: "promotion-status-column",
                }
            ],
            customActions: [
                {
                    title: "Delete Expired Promotions",
                    icon: "fa-trash-alt",
                    class: "delete-btn text-white font-bold",
                    action: this.openDeleteExpiredConfirmation
                }
            ]
        }
    },
    computed: {
        ...mapState("misc", ["warehouses", "customerGroups", "productsForSelection"]),
        ...mapState("auth", ["user"]),
        canEditPricingPolicy() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_PROMOTION,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
    },
    components: { CrudIndex, CustomModal },
    async mounted() {
        await Promise.all([this.setupForm(), this.fetchPricingConfig()])
    },
    watch: {
        warehouses(e) {
            this.setupForm()
        },
    },
    methods: {
        copyPromotion(promotion) {
            this.$router.push({
                name: "promotion_create",
                query: { copyFrom: String(promotion.id) },
            })
        },
        async fetchPricingConfig() {
            this.pricingConfigLoading = true
            try {
                const response = await http.get("/promotions/config")
                this.pricingMode = response.data.pricing_mode || "best_price"
            } catch (error) {
                this.$toast.error(error.response?.data?.message || "Could not load promotion pricing policy")
            } finally {
                this.pricingConfigLoading = false
            }
        },
        async savePricingMode(pricingMode) {
            if (!this.canEditPricingPolicy || pricingMode === this.pricingMode || this.pricingConfigSaving) return
            const previousMode = this.pricingMode
            this.pricingMode = pricingMode
            this.pricingConfigSaving = true
            try {
                const response = await http.patch("/promotions/config", { pricing_mode: pricingMode })
                this.pricingMode = response.data.pricing_mode
                this.$toast.success(`Promotion pricing set to ${pricingMode === "cumulative" ? "Cumulative" : "Best price"}`)
            } catch (error) {
                this.pricingMode = previousMode
                this.$toast.error(error.response?.data?.message || "Could not update promotion pricing policy")
            } finally {
                this.pricingConfigSaving = false
            }
        },
        /**
         * Opens the confirmation modal for deleting expired promotions
         * First fetches the count of expired promotions to show in the confirmation
         */
        async openDeleteExpiredConfirmation() {
            // Reset modal state
            this.showDeleteConfirmModal = true;
            this.loadingExpiredCount = true;
            this.expiredPromotionsCount = null;
            this.previewError = null;
            this.deletingExpired = false;

            try {
                // Fetch the count of expired promotions
                const response = await http.get('/promotions/expired/preview');
                
                if (response.status === 200) {
                    this.expiredPromotionsCount = response.data.count;
                    this.loadingExpiredCount = false;
                } else {
                    throw new Error('Failed to fetch expired promotions count');
                }
            } catch (error) {
                console.error("Error fetching expired promotions count:", error);
                this.previewError = error.response?.data?.message || "Failed to fetch expired promotions count";
                this.loadingExpiredCount = false;
            }
        },

        /**
         * Cancels the delete expired promotions action and closes the modal
         */
        cancelDeleteExpired() {
            this.showDeleteConfirmModal = false;
            // Reset all modal state
            this.loadingExpiredCount = false;
            this.expiredPromotionsCount = null;
            this.previewError = null;
            this.deletingExpired = false;
        },

        /**
         * Confirms and executes the deletion of expired promotions
         */
        async confirmDeleteExpired() {
            this.deletingExpired = true;

            try {
                const currentDate = new Date().toISOString();
                const response = await http.delete(`/promotions/expired?currentDate=${currentDate}`);
                
                if (response.status === 200) {
                    const deletedCount = response.data.count;
                    this.$toast.success(
                        deletedCount > 0 
                            ? `Successfully deleted ${deletedCount} expired promotion${deletedCount > 1 ? 's' : ''}`
                            : "No expired promotions were found to delete"
                    );
                    
                    // Reload the data to reflect changes
                    this.$refs.crudIndex.fetchData();
                    
                    // Close the modal
                    this.showDeleteConfirmModal = false;
                } else {
                    throw new Error('Failed to delete expired promotions');
                }
            } catch (error) {
                console.error("Error deleting expired promotions:", error);
                this.$toast.error(
                    error.response?.data?.message || "Failed to delete expired promotions"
                );
            } finally {
                this.deletingExpired = false;
                // Reset modal state
                this.loadingExpiredCount = false;
                this.expiredPromotionsCount = null;
                this.previewError = null;
            }
        },
        async setupForm() {
            this.loading = true
            await this.fetchFormInitData()

            const warehouses = [{ id: "", title: "All" }, ...this.warehouses]
            const customerGroups = [{ id: "", title: "All" }, ...this.customerGroups]
            const productsForSelection = [{ id: "", title: "All" }, ...this.productsForSelection]

            this.createFields = [
                createColumn("Name*", "name", "text", true),
                createField("Reward Type", "isPercentage", "select", false, 0, [
                    {
                        id: 0,
                        title: "$",
                    },
                    {
                        id: 1,
                        title: "%",
                    },
                ]),
                createColumn("Reward value*", "rewardValue", "number", true),
                createField("Warehouses", "warehouseIds", "multiselect", true, "", warehouses),
                createField("Customer Groups", "customerGroupIds", "multiselect", true, "", customerGroups),
                createField("Products", "productIds", "multiselect", true, "", this.productsForSelection),
                createField("Active", "active", "switch", false, true),
            ]
            setTimeout(() => {
                this.loading = false
            }, 0)
        },
        cleanPromotionName(name) {
            return String(name || "Untitled promotion")
                .replace(/\bundefined\b/gi, "")
                .replace(/\s{2,}/g, " ")
                .trim() || "Untitled promotion"
        },
        promotionTypeLabel(promotionType) {
            const labels = {
                bogo_discounted: "BOGO",
                bundle_discount: "Bundle",
                product_discount: "Product discount",
                first_order: "First order",
            }
            if (labels[promotionType]) return labels[promotionType]
            return String(promotionType || "Promotion")
                .replace(/_/g, " ")
                .replace(/\b\w/g, (letter) => letter.toUpperCase())
        },
        promotionRewardValue(value) {
            const number = Number(value)
            if (!Number.isFinite(number)) return String(value || "0")
            return number.toLocaleString("en-CA", {
                minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
                maximumFractionDigits: 2,
            })
        },
        promotionDiscountSummary(item) {
            const rewardValue = this.promotionRewardValue(item.rewardValue)
            if (item.rewardApplication === "percentage_off") return `${rewardValue}% off`
            if (item.rewardApplication === "fixed_price") return `Fixed at $${rewardValue}`
            return `$${rewardValue} off`
        },
        promotionLocationDetails(warehouseIds) {
            const rawIds = Array.isArray(warehouseIds)
                ? warehouseIds
                : String(warehouseIds ?? "").split(",")
            const ids = rawIds
                .map((value) => String(value?.id ?? value).trim())
                .filter((value) => value && value.toLowerCase() !== "null")

            if (!ids.length) {
                return {
                    summary: "All",
                    details: "All locations",
                }
            }

            const warehouseNames = new Map(
                this.warehouses.map((warehouse) => [
                    String(warehouse.id),
                    warehouse.name || warehouse.title || "Unknown location",
                ])
            )
            const names = ids.map((id) => warehouseNames.get(id) || "Unknown location")

            return {
                summary: names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`,
                details: names.join(", "),
            }
        },
        formatPromotions(items = []) {
            return items.map((item) => {
                const locations = this.promotionLocationDetails(item.warehouseIds)
                const lifecycleStatus = ["active", "inactive", "scheduled", "expired"].includes(item.lifecycleStatus)
                    ? item.lifecycleStatus
                    : (item.active ? "active" : "inactive")
                const lifecyclePresentation = {
                    active: {
                        label: "Active",
                        className: "is-positive",
                        details: "Enabled and within its configured schedule.",
                    },
                    inactive: {
                        label: "Inactive",
                        className: "is-muted",
                        details: "Manually disabled.",
                    },
                    scheduled: {
                        label: "Scheduled",
                        className: "is-scheduled",
                        details: "Enabled and scheduled to begin in the future.",
                    },
                    expired: {
                        label: "Expired",
                        className: "is-expired",
                        details: "Enabled, but every configured schedule has ended.",
                    },
                }[lifecycleStatus]
                return {
                    ...item,
                    name: this.cleanPromotionName(item.name),
                    promotionTypeLabel: this.promotionTypeLabel(item.promotionType),
                    discountSummary: this.promotionDiscountSummary(item),
                    posVisibilityLabel: item.showOnSalesDashboard ? "Shown" : "Hidden",
                    posVisibilityClass: item.showOnSalesDashboard ? "is-positive" : "is-muted",
                    locationSummary: locations.summary,
                    locationDetails: locations.details,
                    statusLabel: lifecyclePresentation.label,
                    statusClass: lifecyclePresentation.className,
                    statusDetails: lifecyclePresentation.details,
                }
            })
        },
        async fetchFormInitData() {
            try {
            } catch (error) {
                console.log(`🚀 ~ file: CustomerForm.vue:242 ~ fetchFormInitData ~ error:`, error)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

::v-deep .vgt-table th,
::v-deep .vgt-table td {
  padding: $tableCellPaddingY $tableCellPaddingX !important;
}

::v-deep .vgt-table {
  width: 100%;
  min-width: 44rem;
  table-layout: fixed;
}

::v-deep .vgt-table thead th {
  height: 3.25rem;
  color: #526159 !important;
  font-size: 0.72rem;
  line-height: 1.2;
  vertical-align: middle !important;
  white-space: nowrap;
}

::v-deep .promotion-name-column {
  width: auto;
}

::v-deep .promotion-discount-column {
  width: 7.5rem;
}

::v-deep .promotion-pos-column {
  width: 4.75rem;
}

::v-deep .promotion-locations-column {
  width: 6.5rem;
  max-width: 6.5rem;
}

::v-deep .promotion-locations-column .crud-cell-primary {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .promotion-status-column {
  width: 6.5rem;
}

::v-deep .crud-actions-column {
  width: 8.25rem;
  min-width: 8.25rem;
}

::v-deep .action-buttons-container {
  gap: 0.35rem;
}

::v-deep .action-buttons-container .action-btn {
  width: 2rem !important;
  min-width: 2rem !important;
  height: 2rem !important;
  padding: 0 !important;
}

::v-deep .action-buttons-container .action-btn i {
  margin-right: 0 !important;
}

::v-deep .promotion-pos-column .crud-cell-primary,
::v-deep .promotion-status-column .crud-cell-primary {
  display: inline-flex;
  align-items: center;
  min-height: 1.55rem;
  padding: 0.18rem 0.48rem;
  color: #58665f;
  background: #eef2f0;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
}

::v-deep .promotion-pos-column .crud-cell-primary.is-positive,
::v-deep .promotion-status-column .crud-cell-primary.is-positive {
  color: #18563f;
  background: #e5f2ec;
}

::v-deep .promotion-status-column .crud-cell-primary.is-scheduled {
  color: #72520b;
  background: #fff3cf;
}

::v-deep .promotion-status-column .crud-cell-primary.is-expired {
  color: #8a2d2d;
  background: #fbe7e7;
}

@media (max-width: 900px) {
  ::v-deep .vgt-table {
    min-width: 0;
  }

  ::v-deep .vgt-table thead {
    display: none;
  }

  ::v-deep .vgt-table,
  ::v-deep .vgt-table tbody {
    display: block;
    width: 100%;
  }

  ::v-deep .vgt-table tbody tr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "promotion promotion"
      "discount pos"
      "locations status"
      "actions actions";
    gap: 0.65rem 1rem;
    height: auto !important;
    padding: 0.9rem;
    border-bottom: 1px solid #e4e9e6;
  }

  ::v-deep .vgt-table tbody td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    padding: 0 !important;
    border: 0 !important;
  }

  ::v-deep .vgt-table tbody td::before {
    margin-right: 0.75rem;
    color: #77827c;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  ::v-deep .promotion-name-column {
    grid-area: promotion;
    padding-bottom: 0.65rem !important;
    border-bottom: 1px solid #edf0ee !important;
  }

  ::v-deep .promotion-name-column::before {
    display: none;
  }

  ::v-deep .promotion-discount-column {
    grid-area: discount;
  }

  ::v-deep .promotion-discount-column::before {
    content: "Discount";
  }

  ::v-deep .promotion-pos-column {
    grid-area: pos;
  }

  ::v-deep .promotion-pos-column::before {
    content: "POS";
  }

  ::v-deep .promotion-locations-column {
    grid-area: locations;
  }

  ::v-deep .promotion-locations-column::before {
    content: "Locations";
  }

  ::v-deep .promotion-status-column {
    grid-area: status;
  }

  ::v-deep .promotion-status-column::before {
    content: "Status";
  }

  ::v-deep .crud-actions-column {
    grid-area: actions;
    position: static !important;
    justify-content: flex-end !important;
    padding-top: 0.25rem !important;
    box-shadow: none !important;
  }
}

.pricing-policy-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 1.35rem 1.5rem;
  margin-bottom: 1.25rem;
  background: #fff;
  border: 1px solid #e5e9e7;
  border-left: 4px solid #1f5d46;
  border-radius: 10px;
  box-shadow: 0 4px 18px rgba(24, 45, 36, 0.06);
}

.eyebrow {
  margin: 0 0 0.25rem;
  color: #1f5d46;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.pricing-policy-panel h2 {
  margin: 0;
  color: #1e2d27;
  font-size: 1.15rem;
}

.policy-copy {
  max-width: 580px;
  margin: 0.35rem 0 0;
  color: #65726c;
  font-size: 0.88rem;
}

.policy-actions {
  min-width: 390px;
}

.policy-actions small {
  display: block;
  margin-top: 0.45rem;
  color: #748078;
  text-align: right;
}

.mode-picker {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  overflow: hidden;
  border: 1px solid #cfd8d3;
  border-radius: 8px;
}

.mode-picker button {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  padding: 0.65rem 1rem;
  color: #4a5a52;
  background: #f7f9f8;
  border: 0;
  cursor: pointer;
}

.mode-picker button + button {
  border-left: 1px solid #cfd8d3;
}

.mode-picker button.active {
  color: #fff;
  background: #1f5d46;
}

.mode-picker button:nth-child(2).active {
  background: #9a6419;
}

.mode-picker button:disabled {
  cursor: default;
}

.mode-picker span {
  font-size: 0.72rem;
  opacity: 0.8;
}

@media (max-width: 900px) {
  .pricing-policy-panel {
    align-items: stretch;
    flex-direction: column;
  }
  .policy-actions {
    min-width: 0;
  }
}
</style>
